import { useCallback, useEffect, useMemo, useState } from 'react'
import { OperationMode } from '../domain/operationMode'
import { GetOperationModesUseCase } from '../usecases/GetOperationModesUseCase'
import { AxiosOperationModeRepository } from '../infrastructure/AxiosOperationModeRepository'
import {
    isSorterMode,
    isClassificationMode,
    requiresChutesConfiguration,
    findOperationModeById,
    findOperationModeByCode,
} from '../../utils/operationModeUtils'

interface UseOperationModesResult {
    operationModes: OperationMode[]
    loading: boolean
    error: string | null
    isSorterMode: (mode: OperationMode | null) => boolean
    isClassificationMode: (mode: OperationMode | null) => boolean
    requiresChutesConfiguration: (mode: OperationMode | null) => boolean
    findById: (id: number) => OperationMode | null
    findByCode: (code: string) => OperationMode | null
    getModeBadge: (id?: number) => string | undefined
}

const repository = new AxiosOperationModeRepository()
const getOperationModesUseCase = new GetOperationModesUseCase(repository)

export const useOperationModes = (): UseOperationModesResult => {
    const [operationModes, setOperationModes] = useState<OperationMode[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetch = async () => {
            setLoading(true)
            try {
                const data = await getOperationModesUseCase.execute()
                setOperationModes(data)
            } catch {
                setError('Error al cargar los modos de operación')
            } finally {
                setLoading(false)
            }
        }
        fetch()
    }, [])

    const findById = useCallback(
        (id: number) => findOperationModeById(operationModes, id),
        [operationModes]
    )

    const findByCode = useCallback(
        (code: string) => findOperationModeByCode(operationModes, code),
        [operationModes]
    )

    const getModeBadge = useCallback(
        (id?: number) => id ? `Modo: ${findOperationModeById(operationModes, id)?.name ?? ''}` : undefined,
        [operationModes]
    )

    return useMemo(() => ({
        operationModes,
        loading,
        error,
        isSorterMode,
        isClassificationMode,
        requiresChutesConfiguration,
        findById,
        findByCode,
        getModeBadge,
    }), [operationModes, loading, error, findById, findByCode, getModeBadge])
}
